import React, { useState } from "react";
import { AlertCircle, AlertTriangle, Check, Loader2 } from "lucide-react";
import cn from "classnames";
import { Card } from "../ui";
import type { BuilderStep } from "../config";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

const CTA_LABELS: Record<BuilderStep, string> = {
  targeting: "Continue to Design",
  design: "Continue to Tracking",
  tracking: "Review and finish",
};

export interface SummaryCardProps {
  activeStep: BuilderStep;
  totalCost: string;
  campaignName: string;
  launchDate: string;
  audiences: string;
  unitsLabel: string;
  unitsValue: string;
  format: string;
  saveStatus?: SaveStatus;
  launchDateWarning?: boolean;
  primaryActionDisabled?: boolean;
  /** Hover tooltip shown over a disabled CTA (Targeting min-gate, AC31). */
  primaryActionTooltip?: { title: string; message: string };
  /**
   * Per-channel, per-step Copilot anchor for the CTA, additive to the
   * evergreen `data-cypress-id="builder-primary-cta"`. Left undefined a
   * channel opts out (no behavior change) — see AdmBuilder for the ADM ids.
   */
  ctaDataCy?: string;
  onPrimaryAction: () => void;
}

const Row = ({ label, value, readout }: { label: string; value: string; readout?: string }) => (
  <div className="flex items-start justify-between gap-4 text-sm leading-5">
    <span className="text-muted-foreground w-28 shrink-0">{label}</span>
    <span className="text-foreground flex-1 text-right font-medium" data-copilot-readout={readout}>
      {value}
    </span>
  </div>
);

const Separator = () => <div className="bg-slate-200 dark:bg-accent h-px w-full" />;

const SummaryCard = ({
  activeStep,
  totalCost,
  campaignName,
  launchDate,
  audiences,
  unitsLabel,
  unitsValue,
  format,
  saveStatus = "idle",
  launchDateWarning,
  primaryActionDisabled,
  primaryActionTooltip,
  ctaDataCy,
  onPrimaryAction,
}: SummaryCardProps) => {
  // Touch/keyboard fallback for the disabled-CTA tooltip: hover shows it on
  // desktop, focus shows it for keyboard users, and tapping the (disabled)
  // button area toggles it on touch — where there is no hover.
  const [tipOpen, setTipOpen] = useState(false);
  const showTooltip = !!primaryActionDisabled && !!primaryActionTooltip;

  return (
    <Card className="flex flex-col gap-5 p-[25px]">
      <div
        className="text-[20px] font-medium leading-[32px] text-[#222336] dark:text-foreground"
        data-cypress-id="summaryTitle"
      >
        Summary
      </div>
      <div className="flex flex-col gap-1">
        <div
          className="text-foreground text-[32px] font-semibold leading-[40px] tracking-[-0.8px]"
          data-cypress-id="totalCostValue"
          data-copilot-readout="total"
        >
          {totalCost}
        </div>
        <div className="text-muted-foreground text-xs font-medium" data-cypress-id="totalCostBeforeTaxesText">
          Total cost before taxes
        </div>
      </div>
      <Separator />
      <div className="flex flex-col gap-2.5">
        <Row label="Campaign name" value={campaignName} />
        <div className="flex items-start justify-between gap-4 text-sm leading-5">
          <span className="text-muted-foreground w-28 shrink-0" data-cypress-id="launchDateTitleInSummaryBlock">
            Launch date
          </span>
          <span
            className="text-foreground flex flex-1 items-center justify-end gap-1 text-right font-medium"
            data-cypress-id="launchDateValueInSummaryBlock"
            data-copilot-readout="launchDate"
          >
            {launchDate}
            {launchDateWarning && <AlertTriangle className="h-4 w-4 shrink-0 text-[#e17100]" />}
          </span>
        </div>
        <Row label="Audiences" value={audiences} />
        <Row label={unitsLabel} value={unitsValue} readout="units" />
        {activeStep !== "targeting" && <Row label="Format" value={format} />}
      </div>
      {saveStatus !== "idle" && (
        <>
          <Separator />
          <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground">
            {saveStatus === "saving" && (
              <>
                <Loader2 className="text-slate-400 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            )}
            {saveStatus === "saved" && (
              <>
                <Check className="h-3.5 w-3.5 text-[#059669] dark:text-success-foreground" />
                Campaign saved
              </>
            )}
            {saveStatus === "error" && (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-destructive">Changes could not be saved</span>
              </>
            )}
          </div>
        </>
      )}
      {/* The CTA is wrapped so the disabled-state tooltip (AC31) can surface on
        hover/focus/tap — a disabled <button> never receives pointer events
        itself. The wrapper is focusable + tappable when disabled so keyboard
        and touch users (no hover) can reveal the reason. */}
      <div
        className="group relative"
        tabIndex={showTooltip ? 0 : undefined}
        onClick={() => showTooltip && setTipOpen((open) => !open)}
        onBlur={() => setTipOpen(false)}
      >
        {showTooltip && (
          <div
            role="tooltip"
            data-cypress-id="builder-primary-cta-tooltip"
            className={cn(
              "pointer-events-none absolute bottom-full left-1/2 mb-2 w-[260px] -translate-x-1/2 flex-col gap-0.5 rounded-lg bg-[#222336] px-3 py-2 text-left text-xs leading-snug text-white shadow-lg group-hover:flex group-focus-within:flex",
              tipOpen ? "flex" : "hidden"
            )}
          >
            <span className="font-semibold">{primaryActionTooltip!.title}</span>
            <span className="text-white/80">{primaryActionTooltip!.message}</span>
            <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-solid border-transparent border-t-[#222336]" />
          </div>
        )}
        <button
          type="button"
          disabled={primaryActionDisabled}
          data-cypress-id="builder-primary-cta"
          data-cy={ctaDataCy}
          onClick={() => {
            if (primaryActionDisabled) return;
            onPrimaryAction();
          }}
          className={cn(
            "flex h-9 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-opacity",
            primaryActionDisabled ? "cursor-not-allowed opacity-50" : "hover:opacity-90"
          )}
        >
          {CTA_LABELS[activeStep]}
        </button>
      </div>
    </Card>
  );
};

export { SummaryCard };
